import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  ogImageAlt,
  noindex = false,
  structuredData,
  publishedTime,
  modifiedTime,
  author,
}) => {
  const siteUrl = typeof window !== 'undefined'
    ? (window.location.hostname.includes('fluxerp.pk') ? 'https://fluxerp.pk'
      : window.location.hostname.includes('fluxerp.qa') ? 'https://fluxerp.qa'
      : 'https://fluxerp.co.uk')
    : 'https://fluxerp.co.uk';

  let currentUrl = siteUrl;
  if (canonical) {
    currentUrl = `${siteUrl}${canonical}`;
  } else if (typeof window !== 'undefined') {
    let path = window.location.pathname;
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    if (path === '/home') path = '/';
    currentUrl = `${siteUrl}${path}`;
  }

  const rawImage = ogImage || `${siteUrl}/icons/icon-512.png`;
  // Ensure OG image is always an absolute URL — relative paths break social crawlers
  const image = rawImage.startsWith('http') ? rawImage : `${siteUrl}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;
  const imageAlt = ogImageAlt || `${title} - FluxERP`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" hreflang="x-default" href={currentUrl.replace('https://fluxerp.pk', 'https://fluxerp.co.uk').replace('https://fluxerp.qa', 'https://fluxerp.co.uk')} />
      <link rel="alternate" hreflang="en-gb" href={currentUrl.replace('https://fluxerp.pk', 'https://fluxerp.co.uk').replace('https://fluxerp.qa', 'https://fluxerp.co.uk')} />
      <link rel="alternate" hreflang="en-pk" href={currentUrl.replace('https://fluxerp.co.uk', 'https://fluxerp.pk').replace('https://fluxerp.qa', 'https://fluxerp.pk')} />
      <link rel="alternate" hreflang="en-qa" href={currentUrl.replace('https://fluxerp.co.uk', 'https://fluxerp.qa').replace('https://fluxerp.pk', 'https://fluxerp.qa')} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="FluxERP" />
      <meta property="og:locale" content="en_GB" />

      {/* Article meta (for blog/content pages) */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@fluxerp" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Misc */}
      <meta name="format-detection" content="telephone=no" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

      {structuredData && (
        Array.isArray(structuredData)
          ? structuredData.map((sd, i) => (
              <script key={i} type="application/ld+json">{JSON.stringify(sd)}</script>
            ))
          : <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
